/**
 * Promoter referral capture
 * ─────────────────────────────────────────────────────────────────────────
 * Lives at the App root. Watches every navigation for `?ref=CODE`, and on
 * each hit:
 *   1. Posts the code to `/api/v2/ref/track` so the backend mints an
 *      httpOnly server-side cookie (30-day sticky).
 *   2. Mirrors the code into localStorage as a UI-only hint (e.g. to show
 *      "You're shopping via PRM-XXXXX" badges).
 *   3. Cleans the `ref` param from the URL so refresh/share doesn't leak it.
 *
 * IMPORTANT: We rely on the SERVER cookie for actual attribution. The
 * localStorage value is for UX only and must never be trusted by the backend.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { promoterApi } from './api';

const LS_KEY = 'su_ref_code';

/**
 * Returns the locally-cached ref hint (UI-only). Use this to show banners,
 * NEVER to determine actual commission attribution.
 */
export function getCachedRefHint() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.code || !obj?.expires_at) return null;
    if (new Date(obj.expires_at).getTime() < Date.now()) {
      localStorage.removeItem(LS_KEY);
      return null;
    }
    return obj.code;
  } catch {
    return null;
  }
}

function setCachedRefHint(code, ttlDays = 30) {
  try {
    const exp = new Date(Date.now() + ttlDays * 86400000);
    localStorage.setItem(LS_KEY, JSON.stringify({ code, expires_at: exp.toISOString() }));
  } catch {
    /* localStorage may be full / disabled — non-fatal */
  }
}

/**
 * Mount-once hook at the App root. Listens for `?ref=` on every navigation.
 *
 * Behavior:
 *   • If `?ref=PRM-XXXXX` present → POST to backend → strip param.
 *   • Otherwise → no-op.
 */
export function useRefCaptureOnNavigate() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('ref');
    if (!code) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await promoterApi.trackRef(code.trim().toUpperCase());
        if (cancelled) return;
        if (res?.data?.code) {
          setCachedRefHint(res.data.code);
        }
      } catch (err) {
        // Unknown / expired code — silently ignore (UX shouldn't break)
        if (err?.response?.status !== 404) {
          // eslint-disable-next-line no-console
          console.warn('[ref] track failed', err?.message);
        }
      } finally {
        if (cancelled) return;
        // Strip `ref` from URL — leave the path + remaining query intact.
        params.delete('ref');
        const remaining = params.toString();
        const clean = `${location.pathname}${remaining ? `?${remaining}` : ''}${location.hash || ''}`;
        window.history.replaceState({}, '', clean);
      }
    })();

    return () => { cancelled = true; };
  }, [location.search, location.pathname, location.hash]);
}
